// Pure data constants + types for QuoteWizard. No React/client code here - // this file is safely importable from both server and client.

import { CALL_TIMES, TIME_SLOTS } from "@/features/leads/shared/list-answers";
import type { LeadCallTime, LeadStartTiming, LeadTimeSlot } from "@/generated/prisma/enums";

export const AUDIENCES = [
  { value: "privat", label: "Privat" },
  { value: "erhverv", label: "Erhverv" },
] as const;
export type Audience = (typeof AUDIENCES)[number]["value"];

export type PrivatTask = "lobende" | "engangs";
export type BusinessTask = "lobende" | "engangs";

// Window cleaning has been shelved as an option on both sides: it could be
// requested here but not selected as a partner service, so leads couldn't be
// matched. The value lives on in ENGANGS_SUBTYPE_TO_SERVICE + label mappings
// so historical leads can still be read.
export type OneTimeType =
  | "almindelig"
  | "hovedrengoering"
  | "flytterengoering"
  | "doedsbo"
  | "efter-haandvaerker"
  | "andet-engangs";

export type BusinessOneTimeType =
  | "hovedrengoering"
  | "efter-haandvaerker"
  | "fraflytning"
  | "andet";

export type BusinessType =
  | "kontor"
  | "butik"
  | "klinik"
  | "industri"
  | "institution"
  | "boligforening"
  | "andet";

export type PrivatFrequency = "ugentligt" | "hver-14-dag" | "maanedligt" | "efter-behov";
export type BusinessFrequency =
  | "dagligt"
  | "2-3x-ugentligt"
  | "ugentligt"
  | "hver-14-dag"
  | "maanedligt";
export type PropertyType = "hus" | "lejlighed" | "raekkehus" | "sommerhus";

// Contact fields are deliberately absent: they are always typed on the page.
export type InitialFields = {
  audience: Audience | null;
  postalCode: string;
  task: PrivatTask | null;
  business: BusinessType | null;
  engangsSubtype: string;
  frequency: string;
  squareMeters: string;
  propertyType: PropertyType | "";
  floor: string;
  startTiming: LeadStartTiming | "";
  startDate: string;
  timeSlots: LeadTimeSlot[];
  preferences: string[];
  notes: string;
};

export const PRIVAT_TASKS: { value: PrivatTask; label: string; body: string; icon: string }[] = [
  {
    value: "lobende",
    label: "Løbende rengøring",
    body: "Fast aftale, fx hver uge eller hver 14. dag.",
    icon: "/images/lineboil/12-trappemoppe-mini.webp",
  },
  {
    value: "engangs",
    label: "Engangsrengøring",
    body: "Én opgave. Fx hovedrengøring, flytning eller dødsbo.",
    icon: "/images/lineboil/05-spray-mini.webp",
  },
];

export const ONE_TIME_TYPES: { value: OneTimeType; label: string; body: string; icon: string }[] = [
  {
    value: "almindelig",
    label: "Almindelig rengøring",
    body: "Helt normal rengøring én gang - fx hvis din faste hjælper er forhindret.",
    icon: "/images/lineboil/18-moppe.webp",
  },
  {
    value: "hovedrengoering",
    label: "Hovedrengøring",
    body: "Grundig gennemgang, inkl. ovn, skabe og vinduer.",
    icon: "/images/lineboil/15-checkmark-mini.webp",
  },
  {
    value: "flytterengoering",
    label: "Flytterengøring",
    body: "Ved fraflytning eller overdragelse. Typisk med garanti.",
    icon: "/images/lineboil/01-flyttekasser-mini.webp",
  },
  {
    value: "doedsbo",
    label: "Dødsbo",
    body: "Boligklargøring efter dødsfald.",
    icon: "/images/lineboil/04-etageejendom-mini.webp",
  },
  {
    value: "efter-haandvaerker",
    label: "Efter håndværker",
    body: "Rengøring efter renovering eller byggerod.",
    icon: "/images/lineboil/18-moppe.webp",
  },
  {
    value: "andet-engangs",
    label: "Andet",
    body: "Beskriv selv hvad der skal laves.",
    icon: "",
  },
];

export const BUSINESS_TYPES: {
  value: BusinessType;
  label: string;
  icon: string;
}[] = [
  { value: "kontor", label: "Kontor", icon: "/images/lineboil/02-kontor-mini.webp" },
  {
    value: "butik",
    label: "Butik / restaurant",
    icon: "/images/lineboil/13-cafe-mini.webp",
  },
  {
    value: "klinik",
    label: "Klinik / sundhed",
    icon: "/images/lineboil/14-klinik-mini.webp",
  },
  {
    value: "industri",
    label: "Industri / lager",
    icon: "/images/lineboil/04-etageejendom-mini.webp",
  },
  {
    value: "institution",
    label: "Institution",
    icon: "/images/lineboil/14-klinik-mini.webp",
  },
  {
    value: "boligforening",
    label: "Boligforening",
    icon: "/images/lineboil/04-etageejendom-mini.webp",
  },
  { value: "andet", label: "Andet", icon: "" },
];

export const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: "lejlighed", label: "Lejlighed" },
  { value: "hus", label: "Hus" },
  { value: "raekkehus", label: "Rækkehus" },
  { value: "sommerhus", label: "Sommerhus" },
];

export const FLOORS: { value: string; label: string }[] = [
  { value: "stuen", label: "Stuen" },
  { value: "1", label: "1. sal" },
  { value: "2", label: "2. sal" },
  { value: "3", label: "3. sal" },
  { value: "4-plus", label: "4. sal eller højere" },
];

export const PRIVAT_FREQUENCIES: { value: PrivatFrequency; label: string }[] = [
  { value: "ugentligt", label: "Hver uge" },
  { value: "hver-14-dag", label: "Hver 14. dag" },
  { value: "maanedligt", label: "Hver måned" },
  { value: "efter-behov", label: "Efter behov" },
];

export const BUSINESS_FREQUENCIES: { value: BusinessFrequency; label: string }[] = [
  { value: "dagligt", label: "Hver dag" },
  { value: "2-3x-ugentligt", label: "Flere gange/uge" },
  { value: "ugentligt", label: "Hver uge" },
  { value: "hver-14-dag", label: "Hver 14. dag" },
  { value: "maanedligt", label: "Hver måned" },
];

export const BUSINESS_TASKS: { value: BusinessTask; label: string; body: string; icon: string }[] =
  [
    {
      value: "lobende",
      label: "Løbende aftale",
      body: "Fast aftale, fx flere gange om ugen eller hver 14. dag.",
      icon: "/images/lineboil/12-trappemoppe-mini.webp",
    },
    {
      value: "engangs",
      label: "Engangsopgave",
      body: "Én opgave. Fx hovedrengøring eller byggerengøring.",
      icon: "/images/lineboil/05-spray-mini.webp",
    },
  ];

export const BUSINESS_ONE_TIME_TYPES: {
  value: BusinessOneTimeType;
  label: string;
  body: string;
  icon: string;
}[] = [
  {
    value: "hovedrengoering",
    label: "Hovedrengøring",
    body: "Grundig gennemgang af jeres lokaler.",
    icon: "/images/lineboil/15-checkmark-mini.webp",
  },
  {
    value: "efter-haandvaerker",
    label: "Efter håndværker",
    body: "Byggerengøring efter renovering eller ombygning.",
    icon: "/images/lineboil/18-moppe.webp",
  },
  {
    value: "fraflytning",
    label: "Fraflytning",
    body: "Når I opsiger lejekontrakten på lokalerne.",
    icon: "/images/lineboil/01-flyttekasser-mini.webp",
  },
  {
    value: "andet",
    label: "Andet",
    body: "Beskriv selv hvad der skal laves.",
    icon: "",
  },
];

// Every value must be one matchCompanyPreferences (preference-matching.ts)
// knows, or the wish silently counts for nothing in the ranking.
export const PRIVAT_PREFERENCES = [
  { value: "dyrevenlig", label: "Allergivenlige midler" },
  { value: "husdyr", label: "Erfaring med husdyr" },
  { value: "fast-person", label: "Samme person hver gang" },
];

export const BUSINESS_PREFERENCES = [
  // Every value must be one matchCompanyPreferences (preference-matching.ts)
  // knows, or the wish silently counts for nothing in the ranking.
  { value: "svanemaerket", label: "Svanemærket rengøring" },
  { value: "overenskomst", label: "Medarbejdere på overenskomst" },
  { value: "samme-team", label: "Samme team hver gang" },
  { value: "fast-kontakt", label: "Fast kontaktperson" },
  { value: "dansktalende", label: "Dansktalende personale" },
  { value: "straffeattest", label: "Straffeattest på personale" },
];

type OptionSet = {
  tasks: readonly { value: PrivatTask; label: string; body: string; icon: string }[];
  subtypes: readonly {
    value: OneTimeType | BusinessOneTimeType;
    label: string;
    body: string;
    icon: string;
  }[];
  frequencies: readonly {
    value: PrivatFrequency | BusinessFrequency;
    label: string;
  }[];
  timeSlots: readonly { value: LeadTimeSlot; label: string }[];
  callTimes: readonly { value: LeadCallTime; label: string }[];
  preferences: readonly { value: string; label: string }[];
};

// Shared by query parsing and the flow so both accept the same answers.
const AUDIENCE_OPTIONS: Record<Audience, OptionSet> = {
  privat: {
    tasks: PRIVAT_TASKS,
    subtypes: ONE_TIME_TYPES,
    frequencies: PRIVAT_FREQUENCIES,
    timeSlots: [],
    callTimes: CALL_TIMES.options,
    preferences: PRIVAT_PREFERENCES,
  },
  erhverv: {
    tasks: BUSINESS_TASKS,
    subtypes: BUSINESS_ONE_TIME_TYPES,
    frequencies: BUSINESS_FREQUENCIES,
    timeSlots: TIME_SLOTS.options,
    callTimes: [],
    preferences: BUSINESS_PREFERENCES,
  },
};

// Until a link or the audience step names an audience, both lists apply.
// Privat comes first so the same input always yields the same order.
export function optionListsFor(audience: Audience | null): OptionSet {
  const sets =
    audience === null
      ? [AUDIENCE_OPTIONS.privat, AUDIENCE_OPTIONS.erhverv]
      : [AUDIENCE_OPTIONS[audience]];
  return {
    tasks: sets.flatMap((s) => s.tasks),
    subtypes: sets.flatMap((s) => s.subtypes),
    frequencies: sets.flatMap((s) => s.frequencies),
    timeSlots: sets.flatMap((s) => s.timeSlots),
    callTimes: sets.flatMap((s) => s.callTimes),
    preferences: sets.flatMap((s) => s.preferences),
  };
}
